import React from 'react';
import { ITableColumn, ITableColumnAttr } from './column';
import { ITableHeader } from './event';
import { TableFontSize } from '.';
import { IPagination } from './pagination';
import { ITableSortValue } from './sort';
import { ITableSummary } from './summary';
import { ITableTree } from './tree';
import { IResizableProps } from '../../resizable';
import { ITableExpand } from './expand';
import { IMultiHeader } from './multi-header';
import { IListItem } from '../../auto-table/model';

// 表格配置项
export interface ITable<T> extends ITableExtension<T> {
  readonly columns: ReadonlyArray<ITableColumn<T>>; // 每列
  readonly dataSource: ReadonlyArray<T>; // 数据
}

export interface ITableExtension<T> extends IMultiHeader<T> {
  readonly rowSelection?: ITableRowSelection<T>;
  readonly pagination?: IPagination; // 不传则不展示分页
  readonly pageSizeOptions?: string[]; // 指定每页可以显示多少条
  readonly loading?: boolean; // 页面是否加载中

  // 表格行的类名
  readonly rowClassName?: string | ((record: T, index: number) => string);

  // 表格行 key 的取值
  // 在树类型渲染中此字段必填
  readonly rowKey?: string;

  // 表格是否可滚动
  readonly scroll?: ITableScroll;

  // 是否显示表头，默认 true
  readonly showHeader?: boolean;

  // 表格标题
  readonly title?: string | ((currentPage?: number) => React.ReactNode);

  // 表格类名
  readonly className?: string;

  // 默认文案设置，目前包括排序、过滤、空数据文案
  readonly locale?: ITableLocale;

  // 表格大小控制，默认是small(受控)
  readonly size?: TableFontSize;

  // 是否展示外边框和列边框
  readonly bordered?: boolean; // 默认显示边框

  // 设置头部行属性
  readonly onHeaderRow?: (
    columns: ReadonlyArray<ITableColumn<T>>,
    index: number,
    column: ITableColumn<T>
  ) => ITableHeader;

  // 设置行属性
  readonly onRow?: (
    record: T,
    rowIndex: number,
    data: ReadonlyArray<T>
  ) => ITableHeader;

  // 分页、排序、筛选变化时触发
  readonly onChange?: (
    pagination: IPagination | undefined,
    filters: null, // 未实现
    sorter: ITableSortValue | null, // 排序值
    extra: ITableChangedExtra<T>
  ) => void;

  // 与antd用法不一致或特有的属性 start =============================

  // 是否显示默认的空提示
  readonly showDefaultEmpty?: boolean;

  readonly renderEmpty?: (emptyText?: React.ReactNode) => React.ReactNode;

  // 表尾合计行
  readonly summary?: ITableSummary<T>;

  // 无边框样式, 默认不启用
  readonly borderLess?: boolean;

  // 条纹状表格, 默认不启用
  readonly striped?: boolean;

  // 默认排序, 不受控
  readonly defaultSort?: ITableSortValue;

  // 是否显示body，默认 true
  // 配合showHeader此处可扩展成固定header, 只滚动body部分
  readonly showBody?: boolean;

  readonly style?: React.CSSProperties;

  // 头部是否固定, 默认不固定
  readonly isStickyHeader?: boolean;

  // div 模拟表格尾部，非table tfoot
  readonly renderFooter?: () => React.ReactNode | string;

  // 支持树类型的数据显示
  readonly tree?: ITableTree;

  // 表头是否开启拖拽控制宽度
  readonly isEnableResize?: boolean;
  /**
   * 拖拽配置
   * 根据初始化的表头自动生成,默认取的配置宽度，如果没有配置宽度会根据布局自动计算
   * minimumWidth 默认50
   * maximumWidth 默认1000
   */
  readonly resizeConfig?: ITableResizeConfig;

  readonly renderIconDown?: (isExpand: boolean) => React.ReactNode;

  // 是否开启表格的行拖拽，需要选中才能拖拽
  readonly isEnableRowDrag?: boolean;

  // 保存拖拽的数据
  readonly onSaveDropData?: (transferData: string) => void;

  readonly onDraggingChange?: (isDragging: boolean) => void;

  // 受控
  readonly isDragging?: boolean;

  // 是否高亮显示
  readonly highlightStyle?: (row: IListItem) => boolean;

  /** 支持展开详情 */
  readonly expandDetail?: ITableExpand<T>;
}

export type ITableResizeConfig = Pick<
  IResizableProps,
  'maximumWidth' | 'minimumWidth'
>;

// 选择功能的配置
export interface ITableRowSelection<T> {
  // 指定选中项的 key 数组，需要和 onChange 进行配合
  readonly selectedRowKeys: ReadonlyArray<string> | ReadonlyArray<number>;

  // 选中项发生变化时的回调
  readonly onChange: (
    selectedRowKeys: ReadonlyArray<string> | ReadonlyArray<number>,
    selectedRows: ReadonlyArray<T>,
    changeRecord: T | undefined,
    checked: boolean,
    isHeaderCheck: boolean
  ) => void;

  // 设置单元格属性
  readonly onCell?: (record: T, rowIndex: number) => ITableColumnAttr;
  // 默认 checkbox
  readonly type?: 'checkbox' | 'radio';

  readonly getCheckboxProps?: (record?: T) => {
    tips?: string;
    disabled?: boolean;
    className?: string;
    indeterminate?: boolean;
    checked?: boolean;
  };

  // 把选择框列固定在左边
  readonly fixed?: boolean;

  // 	自定义列表选择框宽度
  readonly columnWidth?: string | number;

  // 禁用选中所有
  readonly disabledSelectAll?: boolean;
}

// 选择的状态枚举
export enum TableRowSelectionAction {
  indeterminate = 'indeterminate',
  checked = 'checked',
  unchecked = 'unchecked',
}

export interface ITableScroll {
  // 设置横向滚动，也可用于指定滚动区域的宽和高，可以设置为像素值，百分比，true 和 'max-content'
  readonly x?: number | string | true;
  readonly y?: number | string | true;

  // 如果使用flex布局，可以结合min height使得table根据内容高度变化，不受height影响
  readonly minY?: number | string | true;
  readonly maxY?: number | string | true;

  // 当分页、排序、筛选变化后是否滚动到表格顶部
  readonly scrollToFirstRowOnChange?: boolean;
}

export interface ITableLocale {
  readonly filterConfirm?: string;
  readonly filterReset?: string;
  readonly emptyText?: string | React.ReactNode;
}

// onchange扩展的动作
export enum TableExtraAction {
  paginate = 'paginate', // 分页
  sort = 'sort', // 排序
  filter = 'filter', // 过滤
}

// 表头动作集合
export interface ITableChangedExtra<T> {
  /**
   * @description
   * TableExtraAction.sort 不建议使用此数据作为依赖
   *  1. 如果是远程排序, 此时数据是全量, 并是排序后的
   *  2. 如果是本地排序, 此时数据是当前分页排序的后的数据
   */
  readonly currentDataSource: ReadonlyArray<T>;
  readonly action: TableExtraAction;
}
